import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Database, 
  Users, 
  Building2, 
  Key, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Shield,
  RefreshCw,
  AlertTriangle,
  Rss,
  BookOpen,
  MessageSquare,
  Share2,
  Heart,
  Dog,
  Zap,
  Megaphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSetup() {
  const [bootstrapKey, setBootstrapKey] = useState('');
  const [isKeyVerified, setIsKeyVerified] = useState(false);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});

  // Verify the bootstrap key by checking a user
  const verifyKey = async () => {
    if (!bootstrapKey.trim()) {
      alert('Please enter the bootstrap key');
      return;
    }
    
    setLoading(prev => ({ ...prev, verify: true }));
    try {
      const response = await fetch(
        `${API_URL}/api/admin/bootstrap/check-user?email=test@test.com&bootstrap_key=${encodeURIComponent(bootstrapKey)}`
      );
      
      // If we get 403, key is wrong. If we get 404 (user not found), key is correct
      if (response.status === 403) {
        setIsKeyVerified(false);
        setResults(prev => ({ ...prev, verify: { success: false, message: 'Invalid bootstrap key' } }));
      } else {
        setIsKeyVerified(true);
        setResults(prev => ({ ...prev, verify: { success: true, message: 'Bootstrap key verified!' } }));
      }
    } catch (error) {
      setResults(prev => ({ ...prev, verify: { success: false, message: error.message } }));
    }
    setLoading(prev => ({ ...prev, verify: false }));
  };

  // Generic POST request handler
  const executeAction = async (actionKey, endpoint, description) => {
    if (!isKeyVerified) {
      alert('Please verify the bootstrap key first');
      return;
    }

    setLoading(prev => ({ ...prev, [actionKey]: true }));
    setResults(prev => ({ ...prev, [actionKey]: null }));

    try {
      const url = `${API_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}bootstrap_key=${encodeURIComponent(bootstrapKey)}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      setResults(prev => ({
        ...prev,
        [actionKey]: {
          success: response.ok,
          message: data.message || (response.ok ? 'Success' : 'Failed'),
          data: data
        }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [actionKey]: { success: false, message: error.message }
      }));
    }
    
    setLoading(prev => ({ ...prev, [actionKey]: false }));
  };

  // Core setup actions
  const coreActions = [
    {
      key: 'seedAll',
      title: 'Seed ALL Structure Data',
      description: 'Run all structural seed operations (clubs, sources, breeds, FAQs, sponsors, forums)',
      endpoint: '/api/admin/seed/all',
      icon: Zap,
      color: 'text-yellow-600',
      highlight: true
    },
    {
      key: 'seedContentAll',
      title: 'Seed ALL Content Data',
      description: 'Seed all content: dog parks, blog posts, adoption agencies, NGOs, adoptable pets',
      endpoint: '/api/admin/seed/content-all',
      icon: Zap,
      color: 'text-orange-600',
      highlight: true
    },
    {
      key: 'createIndexes',
      title: 'Create Database Indexes',
      description: 'Creates indexes for optimal query performance (run once per new database)',
      endpoint: '/api/admin/db/create-indexes',
      icon: Database,
      color: 'text-green-600'
    },
    {
      key: 'migrateClubs',
      title: 'Migrate Club Schema',
      description: 'Backfills new fields (motto, why_create, agreements) on existing clubs',
      endpoint: '/api/admin/db/migrate-clubs',
      icon: RefreshCw,
      color: 'text-purple-600'
    }
  ];

  // Content seeding actions
  const contentActions = [
    {
      key: 'seedClubs',
      title: 'Seed Clubs',
      description: 'Creates AADC, WorldPets Global, and Club Canino Hispano',
      endpoint: '/api/admin/seed/clubs',
      icon: Building2,
      color: 'text-blue-600'
    },
    {
      key: 'seedClubAnnouncements',
      title: 'Seed Club Announcements',
      description: 'Create welcome announcements for clubs that dont have one',
      endpoint: '/api/admin/seed/club-announcements',
      icon: Megaphone,
      color: 'text-orange-600'
    },
    {
      key: 'seedExternalSources',
      title: 'Seed External Sources',
      description: 'Pet content sources (Twitter, Instagram, websites) for the public feed',
      endpoint: '/api/admin/seed/external-sources',
      icon: Share2,
      color: 'text-pink-600'
    },
    {
      key: 'seedCuratedSources',
      title: 'Seed Curated Sources',
      description: 'YouTube channels, blogs, educational sites for the Feed Directory',
      endpoint: '/api/admin/seed/curated-sources',
      icon: BookOpen,
      color: 'text-indigo-600'
    },
    {
      key: 'seedBreeds',
      title: 'Seed Breeds',
      description: 'AKC-recognized breeds and common mixed breeds database',
      endpoint: '/api/admin/seed/breeds',
      icon: Dog,
      color: 'text-amber-600'
    },
    {
      key: 'seedFaqs',
      title: 'Seed FAQs',
      description: 'Default frequently asked questions and answers',
      endpoint: '/api/admin/seed/faqs',
      icon: MessageSquare,
      color: 'text-cyan-600'
    },
    {
      key: 'seedSponsors',
      title: 'Seed Sponsors',
      description: 'Partner and sponsor data for the sponsors page',
      endpoint: '/api/admin/seed/sponsors',
      icon: Heart,
      color: 'text-red-600'
    },
    {
      key: 'seedForumCategories',
      title: 'Seed Forum Categories',
      description: 'Default categories for private member forums',
      endpoint: '/api/admin/seed/forum-categories',
      icon: MessageSquare,
      color: 'text-teal-600'
    },
    {
      key: 'seedSocialChannels',
      title: 'Seed Social Channels',
      description: 'Official social media channel links',
      endpoint: '/api/admin/seed/social-channels',
      icon: Share2,
      color: 'text-violet-600'
    }
  ];

  // Content seeding actions (actual content like parks, posts, etc.)
  const contentDataActions = [
    {
      key: 'seedDogParks',
      title: 'Seed Dog Parks',
      description: '35+ real US dog parks across major cities (NYC, LA, Chicago, etc.)',
      endpoint: '/api/admin/seed/dog-parks',
      icon: Dog,
      color: 'text-green-600'
    },
    {
      key: 'seedBlogPosts',
      title: 'Seed Blog Posts',
      description: 'Welcome post, tips articles, training guides',
      endpoint: '/api/admin/seed/blog-posts',
      icon: BookOpen,
      color: 'text-blue-600'
    },
    {
      key: 'seedAdoptionAgencies',
      title: 'Seed Adoption Agencies',
      description: 'Rescue organizations and shelters database',
      endpoint: '/api/admin/seed/adoption-agencies',
      icon: Heart,
      color: 'text-pink-600'
    },
    {
      key: 'seedNgos',
      title: 'Seed NGOs',
      description: 'Regional animal welfare organizations',
      endpoint: '/api/admin/seed/ngos',
      icon: Building2,
      color: 'text-purple-600'
    },
    {
      key: 'seedAdoptablePets',
      title: 'Seed Adoptable Pets',
      description: 'Sample adoptable pets for the adoption section',
      endpoint: '/api/admin/seed/adoptable-pets',
      icon: Dog,
      color: 'text-amber-600'
    }
  ];

  // Import actions
  const importActions = [
    {
      key: 'importRssAll',
      title: 'Import All RSS Feeds',
      description: 'Fetch new articles from all active RSS news sources',
      endpoint: '/api/admin/import/rss-all',
      icon: Rss,
      color: 'text-orange-600'
    }
  ];

  const ResultDisplay = ({ result }) => {
    if (!result) return null;
    
    return (
      <Alert className={`mt-3 ${result.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
        <AlertDescription className="flex items-start gap-2">
          {result.success ? (
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          ) : (
            <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          )}
          <div className="text-sm w-full">
            <p className={result.success ? 'text-green-800' : 'text-red-800'}>
              {result.message}
            </p>
            {result.data?.summary && (
              <pre className="mt-2 text-xs bg-white/50 p-2 rounded overflow-x-auto max-h-40">
                {JSON.stringify(result.data.summary, null, 2)}
              </pre>
            )}
            {result.data?.results && !result.data?.summary && (
              <pre className="mt-2 text-xs bg-white/50 p-2 rounded overflow-x-auto max-h-40">
                {JSON.stringify(result.data.results, null, 2)}
              </pre>
            )}
            {result.data?.errors && result.data.errors.length > 0 && (
              <div className="mt-2 text-xs text-red-700">
                <strong>Errors:</strong>
                <pre className="bg-red-100 p-2 rounded overflow-x-auto max-h-32">
                  {JSON.stringify(result.data.errors, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  };

  const ActionCard = ({ action }) => {
    const Icon = action.icon;
    return (
      <Card 
        className={`transition-all ${isKeyVerified ? 'opacity-100' : 'opacity-50'} ${action.highlight ? 'border-2 border-yellow-400 bg-yellow-50/50' : ''}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg bg-gray-100 ${action.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{action.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{action.description}</p>
              </div>
            </div>
            <Button
              onClick={() => executeAction(action.key, action.endpoint, action.title)}
              disabled={!isKeyVerified || loading[action.key]}
              className={`flex-shrink-0 ${action.highlight ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              data-testid={`${action.key}-btn`}
            >
              {loading[action.key] ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {loading[action.key] ? 'Running...' : 'Run'}
            </Button>
          </div>
          <ResultDisplay result={results[action.key]} />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
            <Shield className="w-8 h-8 text-amber-700" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Setup</h1>
          <p className="text-gray-600 mt-2">Database seeding and migration tools for AADC</p>
        </div>

        {/* Warning */}
        <Alert className="border-amber-500 bg-amber-50">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            These operations modify the database. Only run them if you know what you're doing.
            Seed operations are idempotent (safe to run multiple times).
          </AlertDescription>
        </Alert>

        {/* Bootstrap Key Input */}
        <Card className="border-2 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Bootstrap Key
            </CardTitle>
            <CardDescription>
              Enter the admin bootstrap key to unlock operations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="bootstrapKey" className="sr-only">Bootstrap Key</Label>
                <Input
                  id="bootstrapKey"
                  type="password"
                  placeholder="Enter bootstrap key..."
                  value={bootstrapKey}
                  onChange={(e) => {
                    setBootstrapKey(e.target.value);
                    setIsKeyVerified(false);
                  }}
                  className="font-mono"
                  data-testid="bootstrap-key-input"
                />
              </div>
              <Button 
                onClick={verifyKey}
                disabled={loading.verify || !bootstrapKey.trim()}
                variant={isKeyVerified ? "outline" : "default"}
                className={isKeyVerified ? "bg-green-50 border-green-500 text-green-700" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="verify-key-btn"
              >
                {loading.verify ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isKeyVerified ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Verified
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
            <ResultDisplay result={results.verify} />
          </CardContent>
        </Card>

        {/* Core Setup Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Core Setup
          </h2>
          <div className="space-y-3">
            {coreActions.map((action) => (
              <ActionCard key={action.key} action={action} />
            ))}
          </div>
        </div>

        {/* Content Seeding Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Structure Seeding (Individual)
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Seed structural data like clubs, sources, breeds. Use "Seed ALL Structure Data" above to run all at once.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {contentActions.map((action) => (
              <ActionCard key={action.key} action={action} />
            ))}
          </div>
        </div>

        {/* Content Data Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Dog className="w-5 h-5" />
            Content Data Seeding
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Seed actual content: dog parks, blog posts, adoption listings. Use "Seed ALL Content Data" above to run all at once.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {contentDataActions.map((action) => (
              <ActionCard key={action.key} action={action} />
            ))}
          </div>
        </div>

        {/* Import Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Rss className="w-5 h-5" />
            Content Import
          </h2>
          <div className="space-y-3">
            {importActions.map((action) => (
              <ActionCard key={action.key} action={action} />
            ))}
          </div>
        </div>

        {/* Admin User Seeding */}
        <Card className={`transition-all ${isKeyVerified ? 'opacity-100' : 'opacity-50'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Seed Admin User
            </CardTitle>
            <CardDescription>
              Create or fix an admin user with full privileges
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AdminUserForm 
              bootstrapKey={bootstrapKey} 
              isKeyVerified={isKeyVerified}
              apiUrl={API_URL}
            />
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 pt-4 pb-8">
          <p>AADC Admin Setup • Production Safe • All operations are idempotent</p>
        </div>
      </div>
    </div>
  );
}

// Separate component for admin user form
function AdminUserForm({ bootstrapKey, isKeyVerified, apiUrl }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const seedAdminUser = async () => {
    if (!email || !password) {
      alert('Email and password are required');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const params = new URLSearchParams({
        bootstrap_key: bootstrapKey,
        email: email,
        password: password,
        ...(username && { username: username })
      });

      const response = await fetch(`${apiUrl}/api/admin/seed/admin-user?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      setResult({
        success: response.ok,
        message: data.message || (response.ok ? 'Admin user created/updated' : 'Failed'),
        data: data
      });
    } catch (error) {
      setResult({ success: false, message: error.message });
    }

    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label htmlFor="adminEmail">Email *</Label>
          <Input
            id="adminEmail"
            type="email"
            placeholder="admin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!isKeyVerified}
            data-testid="admin-email-input"
          />
        </div>
        <div>
          <Label htmlFor="adminPassword">Password *</Label>
          <Input
            id="adminPassword"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={!isKeyVerified}
            data-testid="admin-password-input"
          />
        </div>
        <div>
          <Label htmlFor="adminUsername">Username</Label>
          <Input
            id="adminUsername"
            type="text"
            placeholder="Optional"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={!isKeyVerified}
            data-testid="admin-username-input"
          />
        </div>
      </div>
      <Button
        onClick={seedAdminUser}
        disabled={!isKeyVerified || loading || !email || !password}
        className="bg-indigo-600 hover:bg-indigo-700"
        data-testid="seed-admin-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Creating...
          </>
        ) : (
          'Create/Update Admin User'
        )}
      </Button>

      {result && (
        <Alert className={`${result.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <AlertDescription className="flex items-start gap-2">
            {result.success ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
            )}
            <div className="text-sm">
              <p className={result.success ? 'text-green-800' : 'text-red-800'}>
                {result.message}
              </p>
              {result.data?.status && (
                <p className="text-xs mt-1 opacity-75">Status: {result.data.status}</p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
